import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * Cookie consent, asked as TWO questions rather than one.
 *
 *   analytics — which pages help drivers and businesses (GA4)
 *   marketing — advertising and measurement (the Meta pixel, when there is one)
 *
 * They are separate because they are separate purposes: allowing us to count
 * page views is not permission to build an advertising profile. Under UK GDPR
 * and PECR the second needs its own consent, and refusing has to be as easy as
 * accepting — hence three buttons of equal weight, not an "Accept" with the
 * refusal buried.
 *
 * Nothing third-party is stored or even fetched until the visitor says yes:
 * index.html sets Consent Mode v2 defaults to "denied" and exposes
 * window.__plLoadGtag / window.__plLoadPixel; this banner flips them.
 *
 * MIGRATION. Before the split the choice was a single `pl_consent` key, and
 * "granted" meant analytics alone — that is what visitors were asked. Those
 * choices are carried forward as analytics-yes / marketing-no and are NOT
 * re-asked. Silently upgrading them to advertising consent is exactly the
 * thing this change exists to prevent.
 *
 * Not rendered under automation (navigator.webdriver), so the build-time
 * prerender snapshots crawlers receive stay free of the banner.
 *
 * Asked BEFORE sign-in, never inside the portal: the question belongs on the
 * public pages and the login screen. Someone who arrives straight into the
 * dashboard (an emailed sign-in link) is simply not asked — consent stays
 * denied and nothing runs, which is the safe default.
 */

// Authenticated shells. Anything else — marketing, jobs, blog, journey, the
// login pages, candidate onboarding — is fair game for the question.
const PORTAL_PREFIXES = [
  '/subcontractor/dashboard', '/admin/dashboard', '/accounting-hub',
  '/management', '/control', '/mcl/', '/sign/',
];
const isPortalPath = (pathname) => {
  const p = String(pathname || '');
  if (p === '/admin/login' || p === '/accountant/login') return false;
  if (p.startsWith('/accountant') || p.startsWith('/admin')) return p !== '/admin/login';
  return PORTAL_PREFIXES.some((x) => p.startsWith(x));
};

const KEY = 'pl_consent_v2';
const LEGACY_KEY = 'pl_consent';
const NONE = { analytics: false, marketing: false, answered: false };

// Keep in step with window.__plReadConsent in index.html, which runs before
// React exists and therefore cannot import this.
export const readConsent = () => {
  let raw = null;
  try { raw = window.localStorage.getItem(KEY); } catch { /* private mode */ }
  if (raw) {
    try {
      const v = JSON.parse(raw);
      return { analytics: v.analytics === true, marketing: v.marketing === true, answered: true };
    } catch { /* corrupt: fall through and ask again */ }
  }
  let legacy = null;
  try { legacy = window.localStorage.getItem(LEGACY_KEY); } catch { /* private mode */ }
  if (legacy === 'granted') return { analytics: true, marketing: false, answered: true };
  if (legacy === 'denied') return { analytics: false, marketing: false, answered: true };
  return NONE;
};

export const hasAnalyticsConsent = () => readConsent().analytics;
export const hasMarketingConsent = () => readConsent().marketing;

export const setConsent = ({ analytics = false, marketing = false } = {}) => {
  try {
    window.localStorage.setItem(KEY, JSON.stringify({ analytics, marketing, ts: Date.now() }));
    // The old key is left alone. It is only ever read as a fallback, and the
    // new key wins, so a stale value cannot resurrect a withdrawn consent.
  } catch { /* private mode: the choice lasts the session */ }

  if (typeof window.gtag === 'function') {
    window.gtag('consent', 'update', {
      analytics_storage: analytics ? 'granted' : 'denied',
      ad_storage: marketing ? 'granted' : 'denied',
      ad_user_data: marketing ? 'granted' : 'denied',
      ad_personalization: marketing ? 'granted' : 'denied',
    });
  }
  if (analytics && typeof window.__plLoadGtag === 'function') window.__plLoadGtag();
  if (marketing && typeof window.__plLoadPixel === 'function') window.__plLoadPixel();
};

// Kept so the older names still work. Analytics-only, as they always meant.
export const grantAnalyticsConsent = () => setConsent({ analytics: true, marketing: false });
export const denyAnalyticsConsent = () => setConsent({ analytics: false, marketing: false });

const BTN = 'rounded-xl px-4 py-2 text-sm font-bold transition';
const QUIET = `${BTN} border border-white/20 text-slate-200 hover:bg-white/10`;

const ConsentBanner = () => {
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (typeof navigator !== 'undefined' && navigator.webdriver) return;
    setOpen(!readConsent().answered);
  }, []);

  if (!open || isPortalPath(pathname)) return null;

  const choose = (choice) => () => { setConsent(choice); setOpen(false); };

  return (
    <div
      role="dialog"
      aria-label="Cookie choices"
      className="pointer-events-none fixed inset-x-0 bottom-0 z-[120] px-3 pb-3 sm:px-4 sm:pb-4"
    >
      <div className="pointer-events-auto mx-auto flex max-w-3xl flex-col gap-3 rounded-2xl border border-white/10 bg-slate-950/95 p-4 text-sm text-slate-100 shadow-2xl backdrop-blur">
        <p className="leading-snug">
          We&apos;d like to use <strong className="font-bold">analytics</strong> cookies to see which pages
          help drivers and businesses, and <strong className="font-bold">marketing</strong> cookies to
          measure our advertising. Neither runs unless you allow it.{' '}
          <a href="https://parcelink.org/privacy" className="underline underline-offset-2 hover:text-white">
            Privacy policy
          </a>
        </p>
        <div className="flex flex-wrap gap-2 sm:justify-end">
          <button type="button" className={QUIET} onClick={choose({ analytics: false, marketing: false })}>
            Reject all
          </button>
          <button type="button" className={QUIET} onClick={choose({ analytics: true, marketing: false })}>
            Analytics only
          </button>
          <button
            type="button"
            className={`${BTN} bg-white text-slate-900 hover:bg-slate-200`}
            onClick={choose({ analytics: true, marketing: true })}
          >
            Allow all
          </button>
        </div>
      </div>
    </div>
  );
};

export default ConsentBanner;
